import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const nav = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function LuxeHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="luxe-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#fbf8f3]/95 backdrop-blur-md border-b border-[#e6dbc7]"
          : "bg-[#fbf8f3]/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      {/* Theme toggle bar removed */}

      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="luxe-brand-link"
          aria-label="MKA Business Solutions — Home"
          className="flex items-center gap-4 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_mka-company/artifacts/324c7vwg_LOGO%20m.jpg"
            alt="MKA Business Solutions"
            className="h-16 md:h-20 w-auto object-contain"
            data-testid="luxe-brand-logo"
          />
          <span className="hidden sm:flex flex-col leading-none border-l border-[#c9a46b]/40 pl-4">
            <span className="font-serif-display font-bold text-xl md:text-2xl tracking-tight text-[#0a1f5c]">
              MKA Business Solutions
            </span>
            <span className="font-fraunces italic text-[13px] text-[#b8964f] mt-1.5 tracking-wide">
              Consulting · Branding · AI
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`luxe-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `luxe-underline font-serif-display text-base tracking-wide transition-colors ${
                  isActive
                    ? "text-[#b8964f] italic"
                    : "text-[#0a1f5c] hover:text-[#b8964f]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="luxe-header-cta"
            onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 bg-[#0a1f5c] text-[#f7f2ea] px-6 py-3 rounded-full font-fraunces text-sm tracking-wide hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all duration-300"
          >
            <span>Request Consultation</span>
          </button>
        </div>

        <button
          data-testid="luxe-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden p-2 -mr-2 text-[#0a1f5c]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="luxe-mobile-menu"
          className="md:hidden bg-[#fbf8f3] border-t border-[#e6dbc7]"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`luxe-mobile-nav-${n.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-serif-display text-2xl ${
                    isActive ? "italic text-[#b8964f]" : "text-[#0a1f5c]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <button
              data-testid="luxe-mobile-cta"
              onClick={() => {
                setOpen(false);
                navigate("/contact");
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0a1f5c] text-[#f7f2ea] px-6 py-4 rounded-full font-fraunces"
            >
              Request Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
